/** @format */

import { Alert, Checkbox, FormControlLabel, Grid } from "@mui/material";
import { FormikProps, useFormik } from "formik";
import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useApiFeedback } from "../../../../app/useApiFeedback";
import StepperButtons from "../../../../common/custom/StepperButtons";
import MessageResponse from "../../../../common/feedback/messageResponse";
import { addAddressDetailsThunk, selectApplicant } from "../../ApplicantSlice";
import SavedApplicationPopUp from "../SavedApplicationPopUp";
import { AddressDto } from "../../Models";
import PostalForm from "./PostalForm";
import ResidentailForm from "./ResidentailForm";

interface Props {
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  activeStep: number;
}
function Address({ setActiveStep, activeStep }: Props) {
  const [isSameAsResidential, setIsSameAsResidential] = useState(false);
  const validationSchema = yup.object({
    city: yup.string().required(),
    code: yup
      .string()
      .required("Postal Code is required")
      .matches(/^[0-9]+$/, "Postal code should contain only digits")
      .max(4, "A valid postal code should have 4 digits")
      .min(4, "A valid postal code should have 4 digits"),
    line1: yup.string().required("Residential address is required"),
    line2: yup.string(),
    streetName: yup.string().required("Street address is required"),
    provinceId: yup
      .number()
      .required("Province is required")
      .positive()
      .min(1, "Provice is required"),
    postalAddress: yup.string().required("Postal address is required"),
    postalCity: yup.string().required("City is required"),
    postalCode: yup
      .string()
      .required("Postal code is required")
      .matches(/^[0-9]+$/, "postal code should contain only digits")
      .max(4, "A valid postal code should have 4 digits")
      .min(4, "A valid postal code should have 4 digits"),
    postalProvinceId: yup
      .number()
      .required("Province is required")
      .positive("Province is required")
      .min(1, "Province is required"),
  });
  const application = useAppSelector(selectApplicant);
  const dispatch = useAppDispatch();
  const [formValues, setFormValues] = useState<AddressDto>();
  const [isPrefilledFromPreviousYear, setIsPrefilledFromPreviousYear] =
    useState<boolean>(false);
  const [isSaveAndClose, setIsSaveAndClose] = useState<boolean>(false);
  const [isSaveAndCloseMessage, setIsSaveAndCloseMessage] =
    useState<string>("");
  const { feedback, handleApiResponse } = useApiFeedback();

  const initialValue = {
    city: "",
    code: "",
    line1: "",
    line2: "",
    streetName: "",
    provinceId: 0,
    postalAddress: "",
    postalCity: "",
    postalCode: "",
    postalProvinceId: 0,
  };
  const formik: FormikProps<AddressDto> = useFormik<AddressDto>({
    initialValues: formValues || initialValue,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values: AddressDto) => {
      // Always hit the upsert endpoint. The backend is idempotent (updates the
      // shared PersonalDetail.Addresses row in place when it exists, inserts
      // once when it doesn't), so this is safe on every click - whether the
      // applicant is brand new, returning with prior-year data pre-filled, or
      // returning with current-year data already saved. The previous
      // "skip-when-not-dirty" shortcut silently failed for returning applicants
      // and left them unable to continue.
      dispatch(addAddressDetailsThunk(values)).then((resp: any) => {
        handleApiResponse(resp, false);
        if (
          resp.type.includes("fulfilled") &&
          (resp.payload?.validation.code === 201 ||
            resp.payload?.validation.code === 200)
        ) {
          // The address was just upserted for the current year so the "carried
          // over from last year" hint no longer applies.
          setIsPrefilledFromPreviousYear(false);
          if (isSaveAndClose) {
            setIsSaveAndCloseMessage(
              "Your application was saved successfully, you can come back later to complete your application. Press close application to log out and keep your information secure"
            );
          } else {
            setActiveStep(activeStep + 1);
          }
        }
      });
    },
  });

  const loadAddresses = () => {
    const progress = application.loginState.accessToken;
    if (progress && progress.application && progress.application.address) {
      setFormValues(progress.application.address);
      setIsPrefilledFromPreviousYear(
        !!progress.application.address.isPrefilledFromPreviousYear
      );
    }
  };

  useEffect(() => {
    loadAddresses();
  }, [application.loginState.accessToken]);

  const handleCheckboxChange = () => {
    setIsSameAsResidential(!isSameAsResidential);
    if (!isSameAsResidential) {
      formik.setFieldValue(
        "postalAddress",
        `${formik.values.line1}, ${formik.values.line2}, ${formik.values.streetName}`
      );
      formik.setFieldValue("postalProvinceId", formik.values.provinceId);
      formik.setFieldValue("postalCity", formik.values.city);
      formik.setFieldValue("postalCode", formik.values.code);
    } else {
      formik.setFieldValue("postalAddress", "");
      formik.setFieldValue("postalProvinceId", 0);
      formik.setFieldValue("postalCity", "");
      formik.setFieldValue("postalCode", "");
    }
  };

  return (
    <Grid container spacing={2}>
      {isSaveAndCloseMessage && isSaveAndCloseMessage !== "" && (
        <SavedApplicationPopUp
          isSaveAndClose={isSaveAndClose}
          message={isSaveAndCloseMessage}
          status={application.applicantCreateState.status}
        />
      )}

      {isPrefilledFromPreviousYear && (
        <Grid item xs={12}>
          <Alert severity="info" variant="standard">
            We've pre-filled your address from your previous application.
            Please review and update anything that has changed, then click
            "Save and Continue" to confirm it for this year.
          </Alert>
        </Grid>
      )}

      <Grid item xs={12}>
        <h4>Residential Address</h4>

        <MessageResponse feedback={feedback} />
      </Grid>

      <Grid item xs={12}>
        <ResidentailForm formik={formik} />
      </Grid>

      <Grid item xs={12}>
        <h4>Postal Address</h4>
        <FormControlLabel
          control={
            <Checkbox
              checked={isSameAsResidential}
              onChange={() => handleCheckboxChange()}
            />
          }
          label="Same as Residential Address?"
        />
      </Grid>

      <Grid item xs={12}>
        <PostalForm formik={formik} />
      </Grid>

      <Grid item xs={12}>
        <StepperButtons
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          formik={formik}
          status={application.applicantCreateState.status}
          isDisabled={false}
          setSaveAndClose={setIsSaveAndClose}
        />
      </Grid>
    </Grid>
  );
}

export default Address;
